(() => {
  class OneTrustManager {
    constructor() {
      window.dataLayer = window.dataLayer || [];

      if (!window.oneTrustTenantId) {
        return window.cpap.logError('OneTrust tenant ID is required and not provided', {
          ga__isUserInteraction: false,
        });
      }

      this.optanonWrapperCount = 0;
      this.hostname = window.location.hostname;
      this.hasAddedKlaviyoFormListener = false;
      this.klaviyoFormCloseSetTimeout = null;

      this.removeInvalidCookies();

      // Load the OneTrust script
      const oneTrustScript = document.createElement('script');

      oneTrustScript.src = `https://cdn.cookielaw.org/consent/${window.oneTrustTenantId}/otSDKStub.js`;
      oneTrustScript.type = 'text/javascript';
      oneTrustScript.charset = 'UTF-8';
      oneTrustScript.dataset.domainScript = window.oneTrustTenantId;

      oneTrustScript.onload = () => {
        this.shopifyTrackingLibraryCallback();
      };

      document.head.appendChild(oneTrustScript);

      if (
        window.cpap.isWebStorageAvailable('sessionStorage') &&
        window.sessionStorage?.getItem('cpap:privacyConsentCompleted') === 'true'
      ) {
        document.body.classList.add('cpap__onetrust-hidden-banner');
      }
    }

    addKlaviyoFormObservers() {
      window.addEventListener('klaviyoForms', (event) => {
        if (event.detail.type === 'open' && !this.hasAddedKlaviyoFormListener) {
          setTimeout(() => {
            this.addKlaviyoFormCloseListener();

            const formCloseButton = document.querySelector('.klaviyo-close-form');

            if (formCloseButton) {
              formCloseButton.addEventListener('click', () => {
                this.removeKlaviyoFormCloseListener();
              });
            }
          }, 500);
        }
      });

      const consentBanners = document.querySelectorAll('#onetrust-banner-sdk, #onetrust-pc-sdk');

      if (consentBanners.length > 0) {
        consentBanners.forEach((consentBanner) => {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.attributeName === 'style') {
                const isVisible = window.getComputedStyle(consentBanner).display !== 'none';

                if (this.klaviyoFormCloseSetTimeout) {
                  clearTimeout(this.klaviyoFormCloseSetTimeout);
                }

                if (isVisible) {
                  this.addStyleForKlaviyoFormHide();
                } else {
                  this.klaviyoFormCloseSetTimeout = setTimeout(() => {
                    if (!this.hasAddedKlaviyoFormListener) {
                      this.addKlaviyoFormCloseListener();
                    }
                    this.removeStyleForKlaviyoFormHide();
                  }, 1000);
                }
              }
            });
          });

          observer.observe(consentBanner, { attributes: true, attributeFilter: ['style'] });

          // Initial check if banner is visible on page load
          if (window.getComputedStyle(consentBanner).display !== 'none') {
            this.addStyleForKlaviyoFormHide();
          }
        });
      }
    }

    addStyleForKlaviyoFormHide() {
      // Check if the style tag already exists to avoid adding it multiple times
      if (!document.querySelector('#kl-private-reset-style')) {
        const style = document.createElement('style');
        style.id = 'kl-private-reset-style';

        style.innerHTML = `
          [class*="kl-private-reset-css"] {
            display: none!important;
          }
        `;

        document.head.appendChild(style);
      }
    }

    removeStyleForKlaviyoFormHide() {
      // Select the style tag by its ID and remove it if it exists
      const style = document.querySelector('#kl-private-reset-style');
      if (style) {
        style.remove();
      }
    }

    shopifyTrackingLibraryCallback() {
      document.addEventListener('DOMContentLoaded', () =>
        window.cpap.dynamicallyLoadStylesheet(window.cpap.cdnSources.onetrustLibraryOverrideCss)
      );

      const onTrackingConsentReady = (callback) => {
        const intervalId = setInterval(() => {
          if (window.Shopify && window.Shopify.trackingConsent && window.OnetrustActiveGroups) {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            callback(window.Shopify.trackingConsent);
          }
        }, 100);

        const timeoutId = setTimeout(() => {
          clearInterval(intervalId);

          window.cpap.logError(
            'Timeout: Shopify trackingConsent is not available after 15 seconds',
            {
              ga__isUserInteraction: false,
            }
          );
        }, 15000);
      };

      onTrackingConsentReady((trackingConsent) => {
        this.updateShopifyConsentStatus();
      });
    }

    parseQueryString(queryString) {
      return queryString.split('&').reduce((acc, pair) => {
        const [key, value] = pair.split('=');
        acc[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
        return acc;
      }, {});
    }

    getCookies(name) {
      const matches = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)', 'g'));
      if (matches) {
        return matches.map((match) => match?.trim()?.split(name + '=')?.[1]);
      }
      return [];
    }

    closeKlaviyoForm(event) {
      const dialogElement = document.querySelector('[role="dialog"][class*="kl-private-reset"]');
      const firstChild = dialogElement.firstElementChild;

      // Check if the click was not on the first child (outside dialog)
      if (event.target !== firstChild && !firstChild.contains(event.target)) {
        event.target.querySelector('.klaviyo-close-form').click();
        this.removeKlaviyoFormCloseListener();
      }
    }

    addKlaviyoFormCloseListener() {
      const klaviyoForm = document.querySelector('[role="dialog"][class*="kl-private-reset"]');

      if (klaviyoForm && !this.hasAddedKlaviyoFormListener) {
        document
          .querySelector('[role="dialog"][class*="kl-private-reset"]')
          .addEventListener('click', this.closeKlaviyoForm.bind(this));
        this.hasAddedKlaviyoFormListener = true;
      }
    }

    removeKlaviyoFormCloseListener() {
      const klaviyoForm = document.querySelector('[role="dialog"][class*="kl-private-reset"]');

      if (klaviyoForm) {
        document
          .querySelector('[role="dialog"][class*="kl-private-reset"]')
          .removeEventListener('click', this.closeKlaviyoForm.bind(this));
      }

      this.hasAddedKlaviyoFormListener = false;
    }

    getInvalidUserCookieStatus(cookies) {
      return cookies.some((cookie) => {
        const parsedCookie = this.parseQueryString(cookie);
        return !window.utid && parsedCookie.consentId && !parsedCookie.consentId.includes('-');
      });
    }

    removeInvalidCookies() {
      const cookies = this.getCookies('OptanonConsent');

      const hasInvalidUserCookie = this.getInvalidUserCookieStatus(cookies);

      if (cookies.length > 1 || hasInvalidUserCookie) {
        this.removeOneTrustCookies();

        let errorMessage = 'OneTrust: Invalid cookies removed';

        const errorDetails = [];

        if (cookies.length > 1) {
          errorDetails.push('multiple cookies');
        }

        if (hasInvalidUserCookie) {
          errorDetails.push('expired user cookie');
        }

        if (errorDetails.length) {
          errorMessage += ` [${errorDetails.join(', ')}]`;
        }

        window.cpap.logError(errorMessage, {
          ga__isUserInteraction: false,
          sentry__contexts: {
            cookies: {
              OptanonConsent: cookies,
              OptanonAlertBoxClosed: this.getCookies('OptanonAlertBoxClosed'),
            },
          },
        });
      }
    }

    removeCookie(name, options = {}) {
      let cookieString = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      if (options.domain) {
        cookieString += `; domain=${options.domain}`;
      }
      document.cookie = cookieString;
    }

    addFooterLinkListener() {
      const links = document.querySelectorAll(
        'div.bottom-footer-bottomnav a[href^="/policies/privacy-policy"]'
      );
      const link = [...links].find((link) =>
        link.textContent.toLowerCase().trim().includes('privacy preferences')
      );

      if (link) {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
            window.OneTrust.ToggleInfoDisplay();
          }
        });
      }
    }

    updateShopifyConsentStatus() {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        const categoriesGranted = window.OnetrustActiveGroups || '';
        const targetingGranted = categoriesGranted.includes('C0004');
        const performanceGranted = categoriesGranted.includes('C0002');

        window.Shopify.customerPrivacy.setTrackingConsent(
          {
            analytics: performanceGranted,
            marketing: targetingGranted,
            preferences: performanceGranted,
            sale_of_data: targetingGranted,
          },
          () => {}
        );
      }
    }

    addConsentChangeListener() {
      window.OneTrust.OnConsentChanged(() => {
        this.updateShopifyConsentStatus();
      });
    }

    removeOneTrustCookies() {
      ['.', '', null].forEach((domainPrefix) => {
        const options = domainPrefix === null ? {} : { domain: domainPrefix + this.hostname };

        if (this.getCookies('OptanonAlertBoxClosed').length) {
          this.removeCookie('OptanonAlertBoxClosed', options);
        }

        if (this.getCookies('OptanonConsent').length) {
          this.removeCookie('OptanonConsent', options);
        }
      });
    }

    addCookieRemoveListener() {
      const links = document.querySelectorAll('a[href="/account/logout"]');
      Array.from(links).forEach((link) => {
        link.addEventListener('click', () => {
          this.removeOneTrustCookies();
        });
      });
    }

    optanonWrapperCallback(callback) {
      if (this.optanonWrapperCount === 0) {
        this.addFooterLinkListener();
        this.addConsentChangeListener();
        this.addCookieRemoveListener();
        this.addKlaviyoFormObservers();
      } else {
        const pushStorageFailureEvent = () => {
          window.cpap.pushDataLayer({ event: 'privacy_consent_session_storage_failure' });
        };

        // Wrap in a 0-millisecond timeout to await the next event loop before checking the
        // cookie. This is to ensure that the cookie is set before checking its existence.
        setTimeout(() => {
          const cookies = this.getCookies('OptanonConsent');

          if (cookies.length < 1) {
            if (window.cpap.isWebStorageAvailable('sessionStorage')) {
              window.sessionStorage.setItem('cpap:privacyConsentCompleted', true);

              if (!window.sessionStorage.getItem('cpap:privacyConsentCompleted')) {
                pushStorageFailureEvent();
              }
            } else {
              pushStorageFailureEvent();
            }
          }
        }, 0);
      }

      this.optanonWrapperCount++;

      if (typeof callback === 'function') {
        callback();
      }
    }
  }

  const oneTrustManager = new OneTrustManager();

  window.OptanonWrapper = (callback) => oneTrustManager.optanonWrapperCallback(callback);
})();
